// Single source of truth for element colours, icon names, level colours and chart colours.
// tint = pastel surface, mid = bars and icon fill, deep = icon detail (light mode).

export const ELEMENT_THEME = {
  overview:    { tint: '#a3e8d6', mid: '#4fc6ac', deep: '#1a6455', glyph: 'overview' },
  finance:     { tint: '#a6e9f0', mid: '#4cc8d5', deep: '#125e67', glyph: 'finance' },
  ownership:   { tint: '#cbb9fa', mid: '#9575f0', deep: '#47338a', glyph: 'ownership' },
  management:  { tint: '#ffc69e', mid: '#ff8a45', deep: '#8c3d12', glyph: 'management' },
  skills:      { tint: '#ffe07a', mid: '#f5bb1f', deep: '#6e5106', glyph: 'skills' },
  procurement: { tint: '#b2d5ff', mid: '#5fa3f5', deep: '#1f4c85', glyph: 'procurement' },
  esd:         { tint: '#ffb6cd', mid: '#f76f9c', deep: '#8c2848', glyph: 'esd' },
  sed:         { tint: '#f2baeb', mid: '#d873cc', deep: '#752d6e', glyph: 'sed' },
  reports:     { tint: '#dde5e8', mid: '#9fb0b6', deep: '#1d2b30', glyph: 'reports' },
  scenario:    { tint: '#ffe07a', mid: '#f5bb1f', deep: '#6e5106', glyph: 'scenario' },
};

// Backend element / group keys (incl. sector variants) → theme key.
const KEY_TO_THEME = {
  ownership: 'ownership',
  management_control: 'management',
  management_control_transport: 'management',
  employment_equity: 'management',
  skills_development: 'skills',
  skills_development_transport: 'skills',
  skills_development_specialised: 'skills',
  procurement: 'procurement',
  preferential_procurement: 'procurement',
  enterprise_development: 'esd',
  supplier_development: 'esd',
  enterprise_supplier_development: 'esd',
  enterprise_development_transport: 'esd',
  sed: 'sed',
  sed_transport: 'sed',
};

export const themeKey = (key) =>
  KEY_TO_THEME[key] || (ELEMENT_THEME[key] ? key : 'overview');

export const themeFor = (key) => ELEMENT_THEME[themeKey(key)];

// Use with className="themed".
export const themeVars = (key) => {
  const t = themeFor(key);
  return { '--t': t.tint, '--pm': t.mid, '--pd': t.deep };
};

// Softened green → red scale. White text passes contrast on every step in both modes.
export const LEVEL_COLOURS = {
  'Level 1': '#2f7d4f',
  'Level 2': '#3a8a52',
  'Level 3': '#4f9654',
  'Level 4': '#6a9a3c',
  'Level 5': '#977f1c',
  'Level 6': '#a5672a',
  'Level 7': '#a85a30',
  'Level 8': '#a64e36',
  'Non-Compliant': '#a92f2f',
};

// Chart.js draws to a canvas and can't read CSS variables. These neutrals read on both
// light and dark backgrounds, so charts look right on screen and in (light) PDFs.
const CHART_TEXT = '#687a80';
const CHART_GRID = 'rgba(104, 122, 128, 0.18)';

export const chartColors = (key) => {
  const t = themeFor(key);
  return {
    fill: t.mid,
    border: t.mid,
    track: 'rgba(104, 122, 128, 0.25)',
    trackBorder: 'rgba(104, 122, 128, 0.45)',
    text: CHART_TEXT,
    grid: CHART_GRID,
  };
};

// Applied once at startup (index.jsx): app font plus neutral text and grid lines for every chart.
export const applyChartDefaults = (Chart) => {
  Chart.defaults.font.family = '"IBM Plex Sans", "Segoe UI", Arial, sans-serif';
  Chart.defaults.color = CHART_TEXT;
  Chart.defaults.borderColor = CHART_GRID;
};
